import { Component, OnInit, OnDestroy, forwardRef, Injector, Input, Output, EventEmitter } from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS } from '@angular/forms';
import { Observable, Observer, of, noop, BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap, catchError } from 'rxjs/operators';

import { TypeaheadMatch } from 'ngx-bootstrap/typeahead/typeahead-match.class';
import { TypeaheadOrder } from 'ngx-bootstrap/typeahead';
import { BsModalRef } from 'ngx-bootstrap/modal';

import { FuncItemIdxAction, SearchFunc } from 'src/app/shared/utils/func';
import { FieldBaseComponent } from 'src/app/shared/components/field-base/field-base.component';

export class SelectBtnAction {
  constructor(
    public title: string,
    public visible: boolean,
    public disabled: boolean,
    public clsIcon: string,
    public clsBtn: string,
    public item: Object,
    public index: number,
    public clickEvent?: FuncItemIdxAction,
  ) { }
}

@Component({
  selector: 'app-field-input-select',
  templateUrl: './field-input-select.component.html',
  styleUrls: ['./field-input-select.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => FieldInputSelectComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => FieldInputSelectComponent), multi: true },
  ],
})
export class FieldInputSelectComponent extends FieldBaseComponent implements OnInit, OnDestroy {
  @Input() container: string = 'body';
  @Input() displayField: string;
  @Input() valueField: string | null;
  @Input() groupField: string | null;
  @Input() placeholder: string | null;
  @Input() rightText: string | null;
  @Input() minLengthToSearch: number = 3;
  @Input() isFirstItemActive: boolean = false;
  @Input() leftBtnActions: SelectBtnAction[] = [];
  @Input() itemTemplate: any = null;

  @Input() set search(fn: SearchFunc) {
    this.setSearchFn(fn);
  }

  // tslint:disable-next-line: no-output-on-prefix
  @Output() onNoResults = new EventEmitter();
  // tslint:disable-next-line: no-output-on-prefix
  @Output() onChangeText = new EventEmitter();
  @Output() addButton: EventEmitter<string> = new EventEmitter();
  @Output() openButton: EventEmitter<any> = new EventEmitter();

  textSearch: string;
  suggestions$: Observable<any[]>;
  sortConfig: TypeaheadOrder = {
    direction: 'asc'
  };

  private searchTerms = new BehaviorSubject<string>('');
  private searchFn: SearchFunc;
  private modalRef: BsModalRef;
  private searchResults: number = 0;

  constructor(
    protected injector: Injector,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.renderer.setStyle(this.elRef.nativeElement, 'width', '100%');

    if (this.displayField) {
      setTimeout(() => {
        this.sortConfig = {
          direction: 'asc',
          field: this.displayField
        };
      });
    }

    if (!this.suggestions$) {
      this.suggestions$ = of([]);
    }

    try {
      this.modalRef = this.injector.get(BsModalRef);
      if (this.modalRef) {
        this.container = 'modal-body';
      }
    } catch (error) {
      console.log(error);
    }
  }

  ngOnDestroy(): void {
    this.searchTerms.complete();
    super.ngOnDestroy();
  }

  private createObservable(): Observable<any[]> {
    return this.searchTerms.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query: string) => {
        if (query && query.trim()) {
          if (query.length >= this.minLengthToSearch) {
            if (!this.searchFn) {
              return of([]);
            }

            return this.searchFn(query).pipe(
              tap(res => {
                this.searchResults = res.length;
                if (res.length === 0) {
                  this.onNoResults.emit();
                }
              }),
              catchError(err => {
                const errorMessage = err && err.message || 'Algo de errado aconteceu ao consultar registros';
                this.errors = {...this.errors, error: errorMessage};
                return of([]);
              })
            );
          } else {
            this.searchResults = 0;
            this.onNoResults.emit();
            return of([]);
          }
        }

        this.searchResults = 0;
        this.onNoResults.emit();
        return of([]);
      })
    );
  }

  private setSearchFn(fn: SearchFunc): void {
    this.searchFn = fn;

    if (this.searchFn && typeof this.searchFn === 'function') {
      this.suggestions$ = this.createObservable();
    } else {
      this.suggestions$ = of([]);
    }
  }

  private checkEmptyValue(): void {
    if ((this.textSearch + '') === '') {
      this.clear();
    }
  }

  private doWriteValue(valueToWrite: any) {
    if (!!valueToWrite && !!this.valueField && !!(valueToWrite instanceof Object)) {
      super.writeValue(valueToWrite[this.valueField]);
    } else {
      super.writeValue(valueToWrite);
    }

    if (!valueToWrite) {
      this.textSearch = '';
    } else {
      this.textSearch = valueToWrite[this.displayField];
    }
  }

  writeValue(value: any): void {
    if (!!this.searchFn && !!value && !(value instanceof Object) && !!this.suggestions$ && !!this.valueField) {
      const searchResult = this.searchFn(value);
      if (!!searchResult && typeof searchResult.subscribe === 'function') {
        this.subscriptions.push(
          searchResult.subscribe((data: any[]) => {
            const writeValue = data.length === 1 ? data[0] : null;
            this.doWriteValue(writeValue);
          })
        );
      }
    } else {
      this.doWriteValue(value);
    }
  }

  setTextSearch(text: string): void {
    this.textSearch = text;
    this.searchTerms.next(text || '');
    this.checkEmptyValue();
    this.onChangeText.emit(text);
  }

  onSelect(event: TypeaheadMatch): void {
    this.writeValue(event.item);
    this.valueChange(this.value);
  }

  onBlur(event: any): void {
    this.checkEmptyValue();
    super.onBlur(event);
  }

  clear(): void {
    this.writeValue(null);
    this.valueChange(null);
  }

  public get showAddButton(): boolean {
    return this.addButton.observers.length > 0 && !!this.textSearch && !this.value && this.searchResults === 0;
  }

  addClick(event: Event): void {
    this.addButton.emit(this.textSearch);
  }

  public get showOpenButton(): boolean {
    return !!this.openButton && this.openButton.observers.length > 0 && !!this.value;
  }

  openClick(event: Event): void {
    this.openButton.emit(this.value);
  }
}
